import { useEffect } from "react";

import { Box, Typography } from "@mui/material";

import useLocalization from "../../hooks/useLocalization";
import useEntity from "../../hooks/useEntity";

import Input from "../elements/Input";
import ImageUpload from "../elements/ImageUpload";
import Switch from "../elements/Switch";
import AddressEditor from "./AddressEditor";
import EditorToolbar from "./EditorToolbar";
import Select from "../elements/Select";

import InfoBox from "../layout/InfoBox";

const ClientEditor = ({ clientId, onSave, sx }) => {
  const {
    getLabel,
    pricingRetailLevels,
    pricingCountryLevels,
    listingRetailLevels,
    listingCountryLevels,
  } = useLocalization();

  const {
    setEntity,
    emptyObject,
    entityData,
    clearEntityData,
    getEntityData,
    validateEntityData,
    updateField,
    message,
    isSuccessful,
    getFieldMessage,
    saved,
  } = useEntity();

  useEffect(() => {
    setEntity("Client");
  }, []);

  useEffect(() => {
    if (!clientId) {
      clearEntityData();
    } else getEntityData(clientId);
  }, [emptyObject]);

  const handleSave = async () => {
    validateEntityData();
  };

  useEffect(() => {
    if (!saved) return;
    onSave();
  }, [saved]);

  const handlePricingOptions = async (field, value) => {
    let pricingOptions = entityData.pricingOptions || {};
    const newValue = value === "not-set" ? null : value;

    if (newValue === null) {
      pricingOptions = null;
    } else {
      pricingOptions = { ...pricingOptions, [field]: newValue };
    }

    await updateField("pricingOptions", pricingOptions);
  };

  const handleListingOptions = async (field, value) => {
    let listingOptions = entityData.listingOptions || {};
    const newValue = value === "not-set" ? null : value;

    if (newValue === null) {
      listingOptions = null;
    } else {
      listingOptions = { ...listingOptions, [field]: newValue };
    }

    await updateField("listingOptions", listingOptions);
  };

  if (!entityData) return;

  return (
    <Box
      width="100%"
      height="100%"
    >
      <EditorToolbar onSave={handleSave} />
      <Box
        width="calc(100% - 2rem)"
        height="calc(100% - 2rem - 30px)"
        padding="1rem"
        display="flex"
        flexDirection="column"
        gap={1}
        overflow="auto"
      >
        <Typography
          sx={{ fontSize: "8px" }}
          textAlign="right"
        >{`id : ${clientId}`}</Typography>

        <Input
          id="clientName"
          label={getLabel("name")}
          value={entityData.name}
          onChange={(newValue) => updateField("name", newValue)}
          required
          fullwidth
          {...getFieldMessage("name")}
        />
        <Input
          id="clientNamefr"
          label={getLabel("name_fr")}
          value={entityData.name_fr}
          onChange={(newValue) => updateField("name_fr", newValue)}
          fullwidth
        />

        <Input
          id="clientCode"
          label="Client Code"
          value={entityData.clientCode || ""}
          onChange={(newValue) => updateField("clientCode", newValue)}
          required
          fullwidth
          {...getFieldMessage("clientCode")}
        />

        <Switch
          label="Active"
          value={!!entityData.isActive}
          onChange={(newValue) => updateField("isActive", newValue)}
        />

        <Switch
          label="Tax Exempt"
          value={entityData.taxExempt || false}
          onChange={(newValue) => updateField("taxExempt", newValue)}
        />

        <Box
          sx={{ display: "flex", flexDirection: "column" }}
          gap={2}
        >
          <InfoBox
            label="Pricing Options"
            sx={{ backgroundColor: "#eee", color: "#000", ...sx }}
          >
            <Select
              label="Retailer Level"
              items={pricingRetailLevels}
              value={
                entityData?.pricingOptions?.retailLevel
                  ? entityData.pricingOptions.retailLevel
                  : "not-set"
              }
              onChange={(newValue) =>
                handlePricingOptions("retailLevel", newValue)
              }
            />
            <Select
              label="Country Level"
              items={pricingCountryLevels}
              value={
                entityData?.pricingOptions?.countryLevel
                  ? entityData.pricingOptions.countryLevel
                  : "not-set"
              }
              onChange={(newValue) =>
                handlePricingOptions("countryLevel", newValue)
              }
            />
          </InfoBox>

          <InfoBox
            label="Listing Options"
            sx={{ backgroundColor: "#eee", color: "#000", ...sx }}
          >
            <Select
              label="Retailer Level"
              items={listingRetailLevels}
              value={
                entityData?.listingOptions?.retailLevel
                  ? entityData.listingOptions.retailLevel
                  : "not-set"
              }
              onChange={(newValue) =>
                handleListingOptions("retailLevel", newValue)
              }
            />
            <Select
              label="Country Level"
              items={listingCountryLevels}
              value={
                entityData?.listingOptions?.countryLevel
                  ? entityData.listingOptions.countryLevel
                  : "not-set"
              }
              onChange={(newValue) =>
                handleListingOptions("countryLevel", newValue)
              }
            />
          </InfoBox>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientEditor;
